import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "What does SAILKHA TRADE do?",
    answer:
      "SAILKHA TRADE is an India-based trading and e-commerce company that sources, supplies, and distributes Electrical products, Hardware, Sanitary ware, Iron & Steel items, and Tiles. We serve businesses through B2B wholesale, product sourcing, and e-commerce solutions for marketplaces like Amazon and Flipkart, with end-to-end support including quality checks, logistics, and global shipping.",
  },
  {
    question: "What does the name 'SAILKHA' mean and where does it come from?",
    answer:
      "The name SAILKHA is drawn from Sailkha — the paternal ancestral hometown that gives the company its roots. It carries a personal, rooted identity that the company has chosen to extend into every brand built under the group, rather than adopting a generic corporate name.",
  },
  {
    question: "What product categories does SAILKHA TRADE deal in?",
    answer:
      "SAILKHA TRADE specialises in five core categories: Electrical products, Hardware, Sanitary ware, Iron & Steel items, and Tiles. From industrial-grade materials to retail-ready inventory, we source premium products at competitive wholesale prices.",
  },
  {
    question: "What is B2B product sourcing in India and how does SAILKHA TRADE help?",
    answer:
      "B2B product sourcing in India involves finding reliable manufacturers and suppliers for bulk orders. SAILKHA TRADE connects businesses with a verified network of Indian suppliers, handling vetting, negotiation, and quality assurance so you get premium products at competitive wholesale prices.",
  },
  {
    question: "Can SAILKHA TRADE help with e-commerce product sourcing for Amazon and Flipkart?",
    answer:
      "Yes. We provide ready-to-sell, quality-checked products tailored for e-commerce platforms like Amazon, Flipkart, and independent online stores. From sourcing to packaging, we ensure your products meet marketplace requirements and stand out.",
  },
  {
    question: "Does SAILKHA TRADE offer international shipping and global trade support?",
    answer:
      "Absolutely. We support global trade with end-to-end logistics, including international shipping, customs documentation, and distribution. Whether you're importing to or exporting from India, we streamline the entire supply chain.",
  },
  {
    question: "How do I start a wholesale trading partnership with SAILKHA TRADE?",
    answer:
      "Getting started is simple — reach out through our contact form or email us at info.sailkha@gmail.com. Share your requirements and we'll provide a tailored sourcing plan with pricing, timelines, and dedicated account support.",
  },
];

export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="faq" className="py-32 bg-background relative">
      <div className="absolute top-0 left-0 w-full h-px bg-border" />

      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-16">
            <span className="inline-block text-xs tracking-wide-elegant uppercase text-muted-foreground mb-6">
              Common Questions
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] font-medium">
              Frequently Asked
              <br />
              <span className="text-muted-foreground">Questions</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Everything you need to know about our product sourcing, trading,
              and e-commerce services.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
              >
                <AccordionItem value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-foreground hover:no-underline hover:text-foreground/80 font-display text-lg py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>

      {/* FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}
